import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { CalendarRange, Download, FileBarChart, Printer } from 'lucide-react';
import { formatMoney, type ReportsView } from '../api';
import { AgingBar, EmptyNote, formatDay, useDeskData } from '../components/desk';
import '../reports.css';
import { useAgentName, withAgent } from '../agent-name';

const bucketLabels: Record<string, string> = {
  current: 'Current', '1-30': '1–30 days', '31-60': '31–60 days', '61-90': '61–90 days', '90+': '90+ days',
};
type Filters = { from: string; to: string; customerId: string; ownerId: string; segment: string; dsoDays: string; contactWindowDays: string };
const emptyFilters: Filters = { from: '', to: '', customerId: '', ownerId: '', segment: '', dsoDays: '90', contactWindowDays: '14' };

function invoiceHref(view: ReportsView, id: string) {
  const invoice = view.invoiceIndex[id];
  return invoice ? `#customers/${encodeURIComponent(invoice.customerId)}` : null;
}
function RecordList({ view, ids, label }: { view: ReportsView; ids: string[]; label?: string }) {
  const unique = [...new Set(ids)];
  if (!unique.length) return <span className="muted">No records</span>;
  return <details className="report-records"><summary>{label ?? `Open ${unique.length} ${unique.length === 1 ? 'record' : 'records'}`}</summary>
    <ul>{unique.map(id => {
      const href = invoiceHref(view, id);
      const invoice = view.invoiceIndex[id];
      return <li key={id}>{href ? <a href={href}>{invoice?.number ?? id}</a> : <code>{id}</code>}</li>;
    })}</ul>
  </details>;
}
function Metric({ label, value, detail, records }: {
  label: string; value: string; detail: string; records?: ReactNode;
}) {
  return <article className="report-metric"><span>{label}</span><strong>{value}</strong><p>{detail}</p>{records}</article>;
}
const shortList = 8;
/** Long per-customer tables open short; every row stays in the DOM so print and "Save as PDF" still carry all of them. */
function ShowAll({ total, open, onOpen, noun }: { total: number; open: boolean; onOpen: () => void; noun: string }) {
  if (total <= shortList || open) return null;
  return <button type="button" className="report-show-all" onClick={onOpen}>Show all {total} {noun}</button>;
}
const rowClass = (index: number, open: boolean, base = '') => [base, !open && index >= shortList ? 'report-more-row' : ''].filter(Boolean).join(' ') || undefined;

/** The four numbers a finance lead reads first, per currency, ahead of the detail tables. */
function Summary({ view }: { view: ReportsView }) {
  const collectedBy = new Map<string, bigint>();
  for (const entry of view.collections.weeks.flatMap(week => week.currencies))
    collectedBy.set(entry.currency, (collectedBy.get(entry.currency) ?? 0n) + BigInt(entry.collected));
  const currencies = [...new Set([...view.aging.map(row => row.currency), ...collectedBy.keys()])];
  if (!currencies.length) return null;
  return <section className="report-summary" aria-label="Summary">{currencies.map(currency => {
    const row = view.aging.find(entry => entry.currency === currency);
    const open = BigInt(row?.openAr ?? '0');
    const overdue = open - BigInt(row?.buckets.find(bucket => bucket.bucket === 'current')?.minor ?? '0');
    const share = open > 0n ? Number((overdue * 100n) / open) : 0;
    const dso = view.dso.find(entry => entry.currency === currency)?.value ?? null;
    const openCount = row?.openCount ?? 0;
    return <div key={currency} className="report-summary-row">
      <div><span>Open AR{currencies.length > 1 ? ` · ${currency}` : ''}</span><strong>{formatMoney(open.toString(), currency)}</strong>
        <small>{openCount} {openCount === 1 ? 'invoice' : 'invoices'}</small></div>
      <div><span>Overdue</span><strong>{formatMoney(overdue.toString(), currency)}</strong><small>{share}% of open AR</small></div>
      <div><span>DSO</span><strong>{dso === null ? '–' : `${dso} days`}</strong><small>Days sales outstanding</small></div>
      <div><span>Collected</span><strong>{formatMoney((collectedBy.get(currency) ?? 0n).toString(), currency)}</strong>
        <small>{formatDay(view.from, view.asOf)} to {formatDay(view.to, view.asOf)}</small></div>
    </div>;
  })}</section>;
}
function csvHref(params: string, table: string) {
  return `/app-api/reports/export?${params}${params ? '&' : ''}table=${table}`;
}

interface CostRow { touches: number; cost: string; collectedUsdMinor: string; centsPerDollar: string | null }
/** `/app-api/reports/channel-costs` (#285): recorded touch costs against cash collected through the agent. */
export interface ChannelCostView {
  from: string; to: string; note: string; line: string; deferredCalls: number;
  total: CostRow & { touchSharePct: Record<string, string | null>; costSharePct: Record<string, string | null> };
  channels: (CostRow & { channel: 'email' | 'call' })[];
  daily: (CostRow & { date: string })[];
  weekly: (CostRow & { weekStart: string })[];
  customers: (CostRow & { customerId: string; customerName: string })[];
  unit: { answeredCalls: number; costPerAnsweredCall: string | null; promises: number; costPerPromise: string | null;
    keptPromises: number; costPerKeptPromise: string | null };
}
const perDollar = (row: CostRow) => row.centsPerDollar === null ? '–' : `${row.centsPerDollar}¢`;

/** Cost per dollar collected, per channel and per customer, from the recorded cost of every email and call. */
function CollectionCosts({ from, to }: { from: string; to: string }) {
  const agent = useAgentName();
  const { data } = useDeskData<ChannelCostView>(`/app-api/reports/channel-costs?${new URLSearchParams({ from, to }).toString()}`);
  if (!data) return null;
  return <section className="report-section" aria-label="Cost per dollar collected">
    <header><h2>Cost per dollar collected</h2><small className="muted">{withAgent(data.note, agent)}</small></header>
    <p><strong>{data.line}</strong></p>
    <div className="report-metrics">
      <Metric label="Total cost" value={data.total.cost} detail={`${data.total.touches} touches`} />
      <Metric label="Cost per dollar" value={perDollar(data.total)} detail={`${formatMoney(data.total.collectedUsdMinor, 'USD')} collected`} />
      <Metric label="Per answered call" value={data.unit.costPerAnsweredCall ?? '–'} detail={`${data.unit.answeredCalls} answered`} />
      <Metric label="Per promise" value={data.unit.costPerPromise ?? '–'} detail={`${data.unit.promises} promises`} />
      <Metric label="Per kept promise" value={data.unit.costPerKeptPromise ?? '–'} detail={`${data.unit.keptPromises} kept`} />
    </div>
    <div className="desk-table-wrap"><table className="desk-table">
      <thead><tr><th>Channel</th><th className="numeric">Touches</th><th className="numeric">Share of touches</th><th className="numeric">Cost</th>
        <th className="numeric">Share of cost</th><th className="numeric">Collected</th><th className="numeric">Cost per dollar</th></tr></thead>
      <tbody>{data.channels.map(row => <tr key={row.channel}>
        <td>{row.channel === 'call' ? 'Calls' : 'Email'}</td><td className="numeric">{row.touches}</td>
        <td className="numeric">{data.total.touchSharePct[row.channel] ?? '–'}%</td><td className="numeric">{row.cost}</td>
        <td className="numeric">{data.total.costSharePct[row.channel] ?? '–'}%</td>
        <td className="numeric">{formatMoney(row.collectedUsdMinor, 'USD')}</td><td className="numeric">{perDollar(row)}</td>
      </tr>)}</tbody>
    </table></div>
    {data.weekly.length > 1 && <div className="desk-table-wrap"><table className="desk-table">
      <thead><tr><th>Week of</th><th className="numeric">Touches</th><th className="numeric">Cost</th><th className="numeric">Collected</th><th className="numeric">Cost per dollar</th></tr></thead>
      <tbody>{data.weekly.map(row => <tr key={row.weekStart}><td>{formatDay(row.weekStart, data.to)}</td><td className="numeric">{row.touches}</td>
        <td className="numeric">{row.cost}</td><td className="numeric">{formatMoney(row.collectedUsdMinor, 'USD')}</td><td className="numeric">{perDollar(row)}</td></tr>)}</tbody>
    </table></div>}
    {data.customers.length > 0 && <details className="report-records"><summary>By customer ({data.customers.length})</summary>
      <div className="desk-table-wrap"><table className="desk-table">
        <thead><tr><th>Customer</th><th className="numeric">Touches</th><th className="numeric">Cost</th><th className="numeric">Collected</th><th className="numeric">Cost per dollar</th></tr></thead>
        <tbody>{data.customers.slice(0, 50).map(row => <tr key={row.customerId}>
          <td><a href={`#customers/${encodeURIComponent(row.customerId)}`}>{row.customerName}</a></td><td className="numeric">{row.touches}</td>
          <td className="numeric">{row.cost}</td><td className="numeric">{formatMoney(row.collectedUsdMinor, 'USD')}</td><td className="numeric">{perDollar(row)}</td>
        </tr>)}</tbody>
      </table></div>
    </details>}
  </section>;
}

export function Reports() {
  const agent = useAgentName();
  const [draft, setDraft] = useState<Filters>(emptyFilters);
  const [applied, setApplied] = useState<Filters>(emptyFilters);
  const [allPayTimes, setAllPayTimes] = useState(false);
  const [allCustomers, setAllCustomers] = useState(false);
  const params = useMemo(() => new URLSearchParams(Object.entries(applied).filter(([, value]) => value !== '')).toString(), [applied]);
  const path = `/app-api/reports${params ? `?${params}` : ''}`;
  const { data: view, error } = useDeskData<ReportsView>(path);
  // The server owns "today", so the first response fills an untouched date range instead of guessing in the browser.
  useEffect(() => {
    if (!view) return;
    setDraft(current => current.from || current.to ? current
      : { ...current, from: view.from, to: view.to });
    setApplied(current => current.from || current.to ? current
      : { ...current, from: view.from, to: view.to });
  }, [view]);
  function apply(event: FormEvent) {
    event.preventDefault();
    setApplied(draft);
  }
  function reset() { setDraft(emptyFilters); setApplied(emptyFilters); }
  const set = (key: keyof Filters) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setDraft(current => ({ ...current, [key]: event.target.value }));
  return <main id="main-content" className="queue-page desk-page reports-page">
    <header className="page-heading"><div><span className="desk-date"><CalendarRange size={13} aria-hidden /> {view ? formatDay(view.asOf) : 'Loading'}</span>
      <h1>Reports<span className="heading-dot" aria-hidden="true">.</span></h1>
      <p>{view ? view.source === 'snapshot' ? 'Read from the daily snapshot for this date.' : 'Calculated from the live ledger at this date.'
        : 'Reading the ledger…'}</p></div></header>
    {error && <div className="alert error" role="alert">{error}</div>}
    <form className="report-filters" onSubmit={apply} aria-label="Report filters">
      <label>From<input type="date" value={draft.from} onChange={set('from')} /></label>
      <label>To<input type="date" value={draft.to} onChange={set('to')} /></label>
      <label>Customer<select value={draft.customerId} onChange={set('customerId')}><option value="">All customers</option>
        {(view?.options.customers ?? []).map(customer => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</select></label>
      <label>Owner<select value={draft.ownerId} onChange={set('ownerId')}><option value="">All owners</option>
        {(view?.options.owners ?? []).map(owner => <option key={owner} value={owner}>{owner}</option>)}</select></label>
      <label>Segment<select value={draft.segment} onChange={set('segment')}><option value="">All segments</option>
        {(view?.options.segments ?? []).map(segment => <option key={segment} value={segment}>{segment}</option>)}</select></label>
      <label>DSO days<input type="number" min="1" max="366" value={draft.dsoDays} onChange={set('dsoDays')} /></label>
      <label>Contact window<input type="number" min="0" max="90" value={draft.contactWindowDays} onChange={set('contactWindowDays')} /></label>
      <div className="report-filter-actions"><button type="submit">Apply</button><button type="button" onClick={reset}>Reset</button></div>
    </form>
    {view && <>
      <div className="report-export"><Download size={14} aria-hidden /><span>CSV</span>
        {['aging', 'customers', 'dso', 'paytimes', 'collections', 'activity', 'trend'].map(table =>
          <a key={table} href={csvHref(params, table)}>{table}</a>)}
        <button type="button" className="report-print" onClick={() => window.print()}><Printer size={14} aria-hidden /> Print or save as PDF</button>
      </div>
      <div className="report-print-header">
        <strong>{view.filters.customerId ? view.options.customers.find(customer => customer.id === view.filters.customerId)?.name ?? 'Customer' : 'All customers'}</strong>
        <span>{view.from} to {view.to}{view.filters.segment ? ` · segment ${view.filters.segment}` : ''}{view.filters.ownerId ? ` · owner ${view.filters.ownerId}` : ''}</span>
        <span>{withAgent(view.collections.note, agent)}</span>
      </div>
      <Summary view={view} />
      {view.unknownBalanceIds.length > 0 && <p className="desk-footnote">{view.unknownBalanceIds.length === 1 ? 'One open invoice has'
        : `${view.unknownBalanceIds.length} open invoices have`} an unknown balance and are left out of the aging and DSO totals.</p>}

      <section className="report-section" aria-label="Aging">
        <header><h2>Aging</h2><small className="muted">Open balances by currency, {formatDay(view.asOf)}</small></header>
        {view.aging.length ? view.aging.map(row => <div key={row.currency} className="report-currency">
          <AgingBar currency={row.currency} buckets={row.buckets} />
          <div className="desk-table-wrap"><table className="desk-table">
            <thead><tr><th>Bucket</th><th className="numeric">Amount</th><th className="numeric">Invoices</th><th>Records</th></tr></thead>
            <tbody>{row.buckets.map(bucket => <tr key={bucket.bucket}>
              <td>{bucketLabels[bucket.bucket] ?? bucket.bucket}</td>
              <td className="numeric">{formatMoney(bucket.minor, row.currency)}</td>
              <td className="numeric">{bucket.count}</td>
              <td><RecordList view={view} ids={bucket.invoiceIds} /></td>
            </tr>)}
            <tr className="report-total"><td>Open AR</td><td className="numeric">{formatMoney(row.openAr, row.currency)}</td>
              <td className="numeric">{row.openCount}</td><td><RecordList view={view} ids={row.openInvoiceIds} label="Open invoices" /></td></tr>
            </tbody>
          </table></div>
        </div>) : <EmptyNote icon={<FileBarChart size={25} aria-hidden />} title="No open invoices">Aging appears once the ledger has open invoices.</EmptyNote>}
      </section>

      <section className="report-section" aria-label="DSO and average days delinquent">
        <header><h2>DSO and average days delinquent</h2><small className="muted">Formula shown with every figure</small></header>
        <div className="report-metrics">{view.dso.map(row => <Metric key={`dso-${row.currency}`} label={`${row.currency} DSO`}
          value={row.value === null ? '–' : `${row.value} days`} detail={row.formula}
          records={<RecordList view={view} ids={[...row.arInvoiceIds, ...row.creditSalesInvoiceIds]} label="Invoices counted" />} />)}
          {view.averageDaysDelinquent.map(row => <Metric key={`add-${row.currency}`} label={`${row.currency} average days delinquent`}
            value={row.value === null ? '–' : `${row.value} days`} detail={row.formula}
            records={<RecordList view={view} ids={row.invoiceIds} label="Overdue invoices counted" />} />)}</div>
        {view.dso.every(row => row.value === null) && <p className="desk-footnote">DSO needs credit sales in the selected period; no currency had any.</p>}
        <h3 className="report-subheading">Average days to pay</h3>
        <p className="desk-footnote">{view.payTimes.formula}.</p>
        {view.payTimes.customers.length ? <div className="desk-table-wrap"><table className="desk-table">
          <thead><tr><th>Customer</th><th>Currency</th><th className="numeric">Invoices paid</th><th className="numeric">Average days to pay</th>
            <th className="numeric">Paid late</th><th>Records</th></tr></thead>
          <tbody>
            {view.payTimes.total.map(row => <tr key={`all-${row.currency}`} className="report-total"><td>All customers</td><td>{row.currency}</td>
              <td className="numeric">{row.paidCount}</td><td className="numeric">{row.averageDaysToPay ?? '–'}</td>
              <td className="numeric">{row.paidLateCount}</td><td><RecordList view={view} ids={row.invoiceIds} label="Invoices" /></td></tr>)}
            {view.payTimes.customers.map((row, index) => <tr key={`${row.customerId}-${row.currency}`} className={rowClass(index, allPayTimes)}>
              <td><a href={`#customers/${encodeURIComponent(row.customerId)}`}>{row.customerName}</a></td><td>{row.currency}</td>
              <td className="numeric">{row.paidCount}</td><td className="numeric">{row.averageDaysToPay ?? '–'}</td>
              <td className="numeric">{row.paidLateCount}</td><td><RecordList view={view} ids={row.invoiceIds} label="Invoices" /></td></tr>)}
          </tbody>
        </table></div> : <p className="desk-footnote">No invoice was fully paid by a recorded payment in this period.</p>}
        <ShowAll total={view.payTimes.customers.length} open={allPayTimes} onOpen={() => setAllPayTimes(true)} noun="rows" />
      </section>

      <section className="report-section" aria-label="Collections">
        <header><h2>Collections</h2><small className="muted">{withAgent(view.collections.note, agent)}</small></header>
        {view.collections.weeks.length ? <div className="desk-table-wrap"><table className="desk-table">
          <thead><tr><th>Week</th><th>Currency</th><th className="numeric">Cash collected</th><th className="numeric">After {agent} contact</th><th>Payments</th></tr></thead>
          <tbody>{view.collections.weeks.flatMap(week => week.currencies.map(currency => <tr key={`${week.weekStart}-${currency.currency}`}>
            <td>{formatDay(week.weekStart, view.asOf)}–{formatDay(week.weekEnd, view.asOf)}</td>
            <td>{currency.currency}</td>
            <td className="numeric">{formatMoney(currency.collected, currency.currency)}</td>
            <td className="numeric">{formatMoney(currency.touched, currency.currency)}<small className="muted">
              {currency.touchedPaymentIds.length} of {currency.paymentIds.length}</small></td>
            <td><RecordList view={view} ids={currency.paymentIds} label="Payments" /></td>
          </tr>))}</tbody>
        </table></div> : <EmptyNote icon={<FileBarChart size={25} aria-hidden />} title="No payments in this period">
          Choose a wider date range to see cash collected.</EmptyNote>}
      </section>

      <CollectionCosts from={view.from} to={view.to} />

      <section className="report-section" aria-label="Activity">
        <header><h2>Activity</h2><small className="muted">{view.activity.from} to {view.activity.to}</small></header>
        <div className="report-metrics">{([
          ['Emails sent', view.activity.emailsSent], ['Calls placed', view.activity.callsPlaced],
          ['Calls answered', view.activity.callsAnswered], ['Replies', view.activity.replies],
          ['Sent to people', view.activity.sentToPeople],
          ['Promises made', view.activity.promisesMade], ['Promises kept', view.activity.promisesKept],
          ['Promises broken', view.activity.promisesBroken], ['Disputes opened', view.activity.disputesOpened],
          ['Disputes resolved', view.activity.disputesResolved],
        ] as const).map(([label, metric]) => <Metric key={label} label={label} value={String(metric.count)}
          detail={`${metric.count} ${metric.count === 1 ? 'record' : 'records'}`}
          records={<RecordList view={view} ids={metric.recordIds} />} />)}</div>
        <p className="desk-footnote">Calls answered are live calls that ended with a recorded result (a promise, dispute, payment claim,
          callback, email request or a request for a person); a conversation with no recorded result is not counted. Sent to people
          counts drafts waiting for a person to approve and calls where the customer asked for one. Promises made, kept and broken come from
          the promise ledger.</p>
      </section>

      <section className="report-section" aria-label="Aging by customer">
        <header><h2>By customer</h2><small className="muted">Every customer and currency keeps its own bucket totals</small></header>
        {view.customerAging.length ? <div className="desk-table-wrap"><table className="desk-table">
          <thead><tr><th>Customer</th><th>Currency</th><th>Segment</th><th className="numeric">Open AR</th>
            <th className="numeric">Overdue</th><th className="numeric">Current</th><th className="numeric">90+</th><th>Records</th></tr></thead>
          <tbody>{view.customerAging.map((row, index) => {
            const current = row.buckets.find(bucket => bucket.bucket === 'current');
            const old = row.buckets.find(bucket => bucket.bucket === '90+');
            return <tr key={`${row.customerId}-${row.currency}`} className={rowClass(index, allCustomers)}>
              <td><a href={`#customers/${encodeURIComponent(row.customerId)}`}>{row.customerName}</a></td>
              <td>{row.currency}</td><td>{row.segments.join(', ') || <span className="muted">–</span>}</td>
              <td className="numeric">{formatMoney(row.openAr, row.currency)}</td>
              <td className="numeric">{formatMoney(row.overdueAr, row.currency)}</td>
              <td className="numeric">{current ? formatMoney(current.minor, row.currency) : '–'}</td>
              <td className="numeric">{old ? formatMoney(old.minor, row.currency) : '–'}</td>
              <td><RecordList view={view} ids={row.openInvoiceIds} label="Invoices" /></td>
            </tr>;
          })}</tbody>
        </table></div> : <EmptyNote icon={<FileBarChart size={25} aria-hidden />} title="No customer balances" />}
        <ShowAll total={view.customerAging.length} open={allCustomers} onOpen={() => setAllCustomers(true)} noun="rows" />
      </section>

      <section className="report-section" aria-label="Aging trend">
        <header><h2>Aging trend</h2><small className="muted">One point per week from stored daily snapshots; the current day is live</small></header>
        {view.trend.length > 1 ? <div className="report-trend">{view.trend.map(point => <div key={point.asOf} className="report-trend-point">
          <strong>{formatDay(point.asOf, view.asOf)}</strong>
          {point.currencies.map(currency => <div key={currency.currency}>
            <span>{currency.currency} · {formatMoney(currency.openAr, currency.currency)}</span>
            <AgingBar compact currency={currency.currency} buckets={currency.buckets} />
          </div>)}
        </div>)}</div> : <EmptyNote icon={<CalendarRange size={25} aria-hidden />} title="A trend needs more daily snapshots">
          Snapshots are taken at the end of each company day. Come back after a few days, or jump the demo clock forward.</EmptyNote>}
      </section>
    </>}
  </main>;
}
